'use client'

import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'

import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Textarea } from '@/components/ui/textarea'
import { useTranslation } from 'react-i18next'

const zelleFormSchema = z.object({
  merchantEmail: z.string().email('Valid email is required').trim(),
  instructionsMessage: z.string().optional(),
})

type FormValues = z.infer<typeof zelleFormSchema>

const defaultInstructionsMessage =
  'Place your order first then follow instructions to pay with Zelle.\n\nPlease send all Zelle Payments To: {merchantEmail}\nPlease use ONLY your Order Number (available once you place order) as the payment memo/reference.\n\nYour order will not be processed until funds have cleared in our Zelle/Bank account.'

interface ZelleFormProps {
  defaultValues?: Record<string, any>
  onSubmit: (data: Record<string, any>) => Promise<void>
  isLoading?: boolean
}

export function ZelleForm({
  defaultValues = {},
  onSubmit,
  isLoading = false,
}: ZelleFormProps) {
  const { t } = useTranslation('integrations')

  const form = useForm<FormValues>({
    resolver: zodResolver(zelleFormSchema),
    defaultValues: {
      merchantEmail: defaultValues.credentials?.merchantEmail ?? '',
      instructionsMessage:
        defaultValues.settings?.instructionsMessage ??
        defaultValues.credentials?.instructionsMessage ??
        defaultInstructionsMessage,
    },
  })

  const handleFormSubmit = async (data: FormValues) => {
    await onSubmit({
      credentials: {
        merchantEmail: data.merchantEmail,
      },
      settings: {
        instructionsMessage: data.instructionsMessage || defaultInstructionsMessage,
      },
    })
  }

  return (
    <form id="integration-config-form" onSubmit={form.handleSubmit(handleFormSubmit)} className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>{t('zelle.configTitle')}</CardTitle>
          <CardDescription>
            {t('zelle.configDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="merchantEmail">{t('zelle.merchantEmail')}</Label>
            <Input
              id="merchantEmail"
              type="email"
              placeholder="support@example.com"
              disabled={isLoading}
              {...form.register('merchantEmail')}
            />
            <p className="text-xs text-muted-foreground">
              {t('zelle.merchantEmailHint')}
            </p>
            {form.formState.errors.merchantEmail && (
              <p className="text-xs text-destructive">{form.formState.errors.merchantEmail.message}</p>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('zelle.instructionsTitle')}</CardTitle>
          <CardDescription>
            {t('zelle.instructionsDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Label htmlFor="instructionsMessage">{t('zelle.message')}</Label>
            <Textarea
              id="instructionsMessage"
              rows={8}
              className="font-mono text-sm"
              disabled={isLoading}
              placeholder={defaultInstructionsMessage}
              {...form.register('instructionsMessage')}
            />
          </div>
        </CardContent>
      </Card>
    </form>
  )
}
